// Список истории: действие — заголовок (когда, кто, что), под ним строки по объектам с полями «с → на».
// Читается как журнал: время первым, колонка нормальной ширины, кнопки стоят сразу за тем, чем управляют
// (приглушены, пока на строку не навели). Один компонент для экрана и для вкладки в карточке.
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { Avatar } from '@core/ui/Avatar';
import { Button } from '@core/ui/Button';
import { cn } from '@core/ui/cn';
import { dateTimeLabel } from '@core/dates';
import { presentRows, type Action, type HistoryRow, type Resolve } from './planner';

export type HistoryRowActions = {
  onOpen?: (row: HistoryRow) => void;                 // карточка объекта; нет — строка не кликается
  onJump?: (row: HistoryRow) => void;                 // «Show on the grid» (задачи роадмапа)
  revert?: (row: HistoryRow) => { label: string; disabled?: string; run: () => void } | null;
  revertAction?: (action: Action) => { disabled?: string; run: () => void } | null;
};

type Props = {
  actions: Action[]; resolve: Resolve; actorOf: (id: string | null) => { name: string; color: string; avatar: string | null };
  hasMore: boolean; loading: boolean; error: string | null; onMore: () => void;
  compact?: boolean;   // вкладка в карточке: без названия объекта в строках
  empty?: ReactNode;
  revertedBy?: ReadonlyMap<number, { id: number; at: string; actor: string | null }>;   // строка журнала → кто и когда её откатил
} & HistoryRowActions;

const kindDot: Record<HistoryRow['kind'], string> = { insert: 'bg-green-500', update: 'bg-blue-500', delete: 'bg-red-500' };
// кнопки видны всегда (клавиатура и тач ничего не теряют), но не спорят с текстом, пока на строку не навели
const ACTIONS = 'flex items-center gap-1 opacity-40 focus-within:opacity-100 transition-opacity';
const BADGE = 'text-xs border rounded px-1';

export function HistoryList({ actions, resolve, actorOf, hasMore, loading, error, onMore, compact, empty, revertedBy, onOpen, onJump, revert, revertAction }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const presented = useMemo(() => new Map(actions.map((a) => [a.tx, presentRows(a, resolve)])), [actions, resolve]); // живой хвост перерисовывает список на каждое событие
  // переход между откатом и откаченной строкой: обе в одном списке, если та уже загружена
  const goto = useCallback((id: number) => {
    const el = root.current?.querySelector<HTMLElement>(`[data-history-row="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.animate([{ backgroundColor: '#fef9c3' }, { backgroundColor: 'transparent' }], { duration: 1400 });
  }, []);
  if (error) return <p role="alert" className="text-danger p-4">{error}</p>;
  if (!loading && actions.length === 0) return <p className="text-ink-muted p-4">{empty ?? 'Nothing here yet.'}</p>;
  return (
    <div ref={root} className={cn('flex flex-col', !compact && 'max-w-5xl')} data-history>
      {actions.map((a) => {
        const who = actorOf(a.actor);
        const ra = a.rows.length > 1 ? revertAction?.(a) : null;
        return (
          <section key={a.tx} data-history-action={a.tx} data-history-op={a.op?.kind} className="group/action border-b border-gray-200 py-2">
            <header className="flex items-center gap-2 flex-wrap px-3 text-sm">
              <time className="text-gray-500 tabular-nums shrink-0" title={a.at}>{dateTimeLabel(a.at)}</time>
              <Avatar name={who.name} color={who.color} src={who.avatar} size={22} />
              <span className="font-semibold">{who.name}</span>
              <span className="text-gray-700">{a.label}</span>
              {(a.op?.kind === 'revert' || a.op?.kind === 'restore') && (() => {
                const undoneAt = typeof a.op['undo_at'] === 'string' ? a.op['undo_at'] : null;
                const undoneBy = typeof a.op['undo_actor'] === 'string' ? actorOf(a.op['undo_actor']).name : null;
                const logId = typeof a.op['log_id'] === 'number' ? a.op['log_id'] : null;
                const what = `${a.op.kind === 'restore' ? 'restores' : 'undoes'}${undoneBy ? ` ${undoneBy}'s change` : ' a change'}${undoneAt ? ` of ${dateTimeLabel(undoneAt)}` : ''}`;
                return logId ? (
                  <button type="button" className={cn(BADGE, 'min-h-0 text-gray-500 border-gray-300 hover:bg-gray-100')} title="Show the change it undoes (if it is loaded)" onClick={() => goto(logId)}>{what}</button>
                ) : <span className={cn(BADGE, 'text-gray-500 border-gray-300')}>{what}</span>;
              })()}
              {ra && (
                <span className={cn(ACTIONS, 'group-hover/action:opacity-100')}>
                  <Button size="sm" variant="ghost" disabled={!!ra.disabled} title={ra.disabled ?? 'Revert the whole action'} onClick={ra.run}>Revert all</Button>
                </span>
              )}
            </header>
            <ul className="mt-0.5">
              {(presented.get(a.tx) ?? []).map(({ row: r, summary: s, group }) => {
                // несколько строк журнала в одной видимой (задача и её назначения) — откат разом, одним вопросом
                const rv = group.length > 1
                  ? (revertAction ? { label: 'Revert', ...revertAction({ ...a, rows: group, label: `${s.title}: ${group.length} rows` }) } : null)
                  : revert?.(group[0] ?? r); // одиночное назначение показано строкой задачи, а откатывать надо настоящую строку
                return (
                  <li key={r.id} data-history-row={r.id} data-kind={r.kind} className="group/row px-3 py-0.5 text-sm hover:bg-gray-50">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn('inline-block w-2 h-2 rounded-full shrink-0', kindDot[r.kind])} aria-hidden />
                      {!compact && (
                        onOpen ? <button type="button" data-history-open className="min-h-0 font-medium text-left hover:underline" onClick={() => onOpen(r)}>{s.title}</button>
                          : <span className="font-medium">{s.title}</span>
                      )}
                      {r.kind === 'delete' && <span className="text-danger">deleted</span>}
                      {r.kind === 'insert' && <span className="text-green-700">created</span>}
                      {s.reordered && <span className="text-gray-500">reordered</span>}
                      {revertedBy?.has(r.id) && (() => {
                        const u = revertedBy.get(r.id)!;
                        return (
                          <button type="button" className={cn(BADGE, 'min-h-0 text-amber-700 border-amber-300 hover:bg-amber-50')} data-reverted
                            title={`Undone by ${actorOf(u.actor).name}, ${dateTimeLabel(u.at)} — show it`} onClick={() => goto(u.id)}>reverted</button>
                        );
                      })()}
                      {/* кнопки сразу за строкой, а не у дальнего края экрана (Глеб, 2026-09-13) */}
                      <span className={cn(ACTIONS, 'group-hover/row:opacity-100')}>
                        {onJump && r.table_name === 'task' && r.kind !== 'delete' && <Button size="sm" variant="ghost" onClick={() => onJump(r)} title="Show on the Projects grid">Show on the grid</Button>}
                        {rv && <Button size="sm" variant="ghost" disabled={!!rv.disabled} title={rv.disabled ?? rv.label} onClick={rv.run} data-history-revert={r.id}>{rv.label}</Button>}
                      </span>
                    </div>
                    {s.fields.map((f) => (
                      <div key={f.field} className="flex gap-2 text-gray-700 pl-4">
                        <span className="w-24 shrink-0 text-gray-500">{f.label}</span>
                        <span className="min-w-0 break-words">
                          {r.kind === 'update' && f.from !== '' ? <><span className="line-through text-gray-400">{f.from}</span> → <span>{f.to}</span></> : f.to}
                        </span>
                      </div>
                    ))}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      <div className="p-3">
        {hasMore ? <Button variant="secondary" size="sm" onClick={onMore} disabled={loading}>{loading ? 'Loading…' : 'Load more'}</Button>
          : loading ? <span className="text-ink-muted text-sm">Loading…</span> : actions.length > 0 && <span className="text-ink-muted text-sm">That's all.</span>}
      </div>
    </div>
  );
}
